import type { SiteNode } from "@zirro/schema";

export interface SectionPreset {
  id: string;
  name: string;
  category: "hero" | "products" | "spotlight" | "trust" | "reviews" | "cta";
  description: string;
  iconName: string;
  badge?: string;
  createSection: () => SiteNode;
}

export const ECOMMERCE_SECTION_PRESETS: SectionPreset[] = [
  {
    id: "ecom_preset_hero",
    name: "E-Commerce Hero Banner",
    category: "hero",
    description: "High-impact hero section with headline, promotion subtext, and dual CTA buttons.",
    iconName: "Sparkles",
    badge: "Popular",
    createSection: (): SiteNode => ({
      id: "sec_ecom_hero",
      type: "Section",
      props: {
        direction: "vertical",
        columns: 1,
        align: "stretch",
        justify: "center",
        gap: "MD",
        padding: "XL",
        width: "contained",
        visibility: "all",
      },
      children: [
        {
          id: "node_hero_h1",
          type: "Heading",
          props: {
            text: "Elevated Essentials for Modern Living",
            level: 1,
            align: "left",
            color: "text",
            fontSize: "5xl",
            weight: 700,
            visibility: "all",
          },
        },
        {
          id: "node_hero_sub",
          type: "Text",
          props: {
            content: [
              {
                text: "Handcrafted everyday objects engineered for longevity, comfort, and understated style.",
              },
            ],
            align: "left",
            color: "muted",
            size: "lg",
            visibility: "all",
          },
        },
        {
          id: "node_hero_btns",
          type: "Container",
          props: {
            direction: "horizontal",
            columns: 2,
            align: "center",
            justify: "start",
            gap: "MD",
            padding: "none",
            visibility: "all",
          },
          children: [
            {
              id: "node_hero_btn1",
              type: "Button",
              props: {
                label: "Shop New Arrivals",
                href: { type: "url", url: "/shop" },
                variant: "primary",
                size: "lg",
                align: "left",
                visibility: "all",
              },
            },
            {
              id: "node_hero_btn2",
              type: "Button",
              props: {
                label: "Explore Lookbook",
                href: { type: "url", url: "/about" },
                variant: "outline",
                size: "lg",
                align: "left",
                visibility: "all",
              },
            },
          ],
        },
      ],
    }),
  },
  {
    id: "ecom_preset_products",
    name: "3-Column Product Showcase",
    category: "products",
    description: "Multi-column product card grid with images, prices, descriptions, and purchase actions.",
    iconName: "ShoppingBag",
    badge: "Core",
    createSection: (): SiteNode => ({
      id: "sec_ecom_prods",
      type: "Section",
      props: {
        direction: "vertical",
        columns: 1,
        align: "stretch",
        justify: "start",
        gap: "LG",
        padding: "XL",
        width: "contained",
        visibility: "all",
      },
      children: [
        {
          id: "node_prods_h2",
          type: "Heading",
          props: {
            text: "Trending Collections",
            level: 2,
            align: "center",
            color: "text",
            fontSize: "4xl",
            weight: 700,
            visibility: "all",
          },
        },
        {
          id: "node_prods_sub",
          type: "Text",
          props: {
            content: [
              {
                text: "Refined everyday pieces crafted with certified sustainable materials.",
              },
            ],
            align: "center",
            color: "muted",
            size: "lg",
            visibility: "all",
          },
        },
        {
          id: "node_prods_grid",
          type: "Container",
          props: {
            direction: "horizontal",
            columns: 3,
            align: "stretch",
            justify: "start",
            gap: "LG",
            padding: "none",
            visibility: "all",
          },
          children: [
            {
              id: "node_p1_card",
              type: "Container",
              props: {
                direction: "vertical",
                columns: 1,
                align: "stretch",
                justify: "between",
                gap: "SM",
                padding: "MD",
                visibility: "all",
              },
              children: [
                {
                  id: "node_p1_img",
                  type: "Image",
                  props: {
                    src: "https://images.unsplash.com/photo-1544816155-12df9643f363?auto=format&fit=crop&w=600&q=80",
                    alt: "Nomad Canvas Weekender Bag",
                    width: "full",
                    align: "center",
                    rounded: true,
                    visibility: "all",
                  },
                },
                {
                  id: "node_p1_title",
                  type: "Heading",
                  props: {
                    text: "Nomad Canvas Weekender",
                    level: 3,
                    align: "left",
                    color: "text",
                    fontSize: "xl",
                    weight: 700,
                    visibility: "all",
                  },
                },
                {
                  id: "node_p1_desc",
                  type: "Text",
                  props: {
                    content: [
                      {
                        text: "Weatherproof waxed canvas with Italian leather handles.",
                      },
                    ],
                    align: "left",
                    color: "muted",
                    size: "base",
                    visibility: "all",
                  },
                },
                {
                  id: "node_p1_price",
                  type: "Text",
                  props: {
                    content: [
                      {
                        text: "$185 USD · In Stock",
                        marks: ["bold"],
                      },
                    ],
                    align: "left",
                    color: "text",
                    size: "lg",
                    visibility: "all",
                  },
                },
                {
                  id: "node_p1_btn",
                  type: "Button",
                  props: {
                    label: "Add to Bag",
                    href: { type: "url", url: "/shop" },
                    variant: "primary",
                    size: "sm",
                    align: "left",
                    visibility: "all",
                  },
                },
              ],
            },
            {
              id: "node_p2_card",
              type: "Container",
              props: {
                direction: "vertical",
                columns: 1,
                align: "stretch",
                justify: "between",
                gap: "SM",
                padding: "MD",
                visibility: "all",
              },
              children: [
                {
                  id: "node_p2_img",
                  type: "Image",
                  props: {
                    src: "https://images.unsplash.com/photo-1514432324607-a09d9b4aefdd?auto=format&fit=crop&w=600&q=80",
                    alt: "Matte Studio Ceramic Tumbler",
                    width: "full",
                    align: "center",
                    rounded: true,
                    visibility: "all",
                  },
                },
                {
                  id: "node_p2_title",
                  type: "Heading",
                  props: {
                    text: "Matte Studio Tumbler",
                    level: 3,
                    align: "left",
                    color: "text",
                    fontSize: "xl",
                    weight: 700,
                    visibility: "all",
                  },
                },
                {
                  id: "node_p2_desc",
                  type: "Text",
                  props: {
                    content: [
                      {
                        text: "Double-wall insulated ceramic with tactile satin finish.",
                      },
                    ],
                    align: "left",
                    color: "muted",
                    size: "base",
                    visibility: "all",
                  },
                },
                {
                  id: "node_p2_price",
                  type: "Text",
                  props: {
                    content: [
                      {
                        text: "$38 USD · In Stock",
                        marks: ["bold"],
                      },
                    ],
                    align: "left",
                    color: "text",
                    size: "lg",
                    visibility: "all",
                  },
                },
                {
                  id: "node_p2_btn",
                  type: "Button",
                  props: {
                    label: "Add to Bag",
                    href: { type: "url", url: "/shop" },
                    variant: "primary",
                    size: "sm",
                    align: "left",
                    visibility: "all",
                  },
                },
              ],
            },
            {
              id: "node_p3_card",
              type: "Container",
              props: {
                direction: "vertical",
                columns: 1,
                align: "stretch",
                justify: "between",
                gap: "SM",
                padding: "MD",
                visibility: "all",
              },
              children: [
                {
                  id: "node_p3_img",
                  type: "Image",
                  props: {
                    src: "https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=600&q=80",
                    alt: "Horizon Automatic Chronograph",
                    width: "full",
                    align: "center",
                    rounded: true,
                    visibility: "all",
                  },
                },
                {
                  id: "node_p3_title",
                  type: "Heading",
                  props: {
                    text: "Horizon Chronograph",
                    level: 3,
                    align: "left",
                    color: "text",
                    fontSize: "xl",
                    weight: 700,
                    visibility: "all",
                  },
                },
                {
                  id: "node_p3_desc",
                  type: "Text",
                  props: {
                    content: [
                      {
                        text: "Sapphire crystal face with Japanese automatic movement.",
                      },
                    ],
                    align: "left",
                    color: "muted",
                    size: "base",
                    visibility: "all",
                  },
                },
                {
                  id: "node_p3_price",
                  type: "Text",
                  props: {
                    content: [
                      {
                        text: "$240 USD · Limited",
                        marks: ["bold"],
                      },
                    ],
                    align: "left",
                    color: "text",
                    size: "lg",
                    visibility: "all",
                  },
                },
                {
                  id: "node_p3_btn",
                  type: "Button",
                  props: {
                    label: "Add to Bag",
                    href: { type: "url", url: "/shop" },
                    variant: "primary",
                    size: "sm",
                    align: "left",
                    visibility: "all",
                  },
                },
              ],
            },
          ],
        },
      ],
    }),
  },
  {
    id: "ecom_preset_trust",
    name: "Trust & Assurance Badges",
    category: "trust",
    description: "3-column customer confidence strip highlighting guarantees, delivery, and ethics.",
    iconName: "ShieldCheck",
    badge: "Trust",
    createSection: (): SiteNode => ({
      id: "sec_ecom_trust",
      type: "Section",
      props: {
        direction: "vertical",
        columns: 1,
        align: "stretch",
        justify: "start",
        gap: "MD",
        padding: "LG",
        width: "contained",
        background: "surface",
        visibility: "all",
      },
      children: [
        {
          id: "node_trust_row",
          type: "Container",
          props: {
            direction: "horizontal",
            columns: 3,
            align: "stretch",
            justify: "start",
            gap: "LG",
            padding: "none",
            visibility: "all",
          },
          children: [
            {
              id: "node_tr1",
              type: "Container",
              props: {
                direction: "vertical",
                columns: 1,
                align: "stretch",
                justify: "start",
                gap: "XS",
                padding: "SM",
                visibility: "all",
              },
              children: [
                {
                  id: "node_tr1_h",
                  type: "Heading",
                  props: {
                    text: "🌿 100% Sustainable",
                    level: 3,
                    align: "left",
                    color: "text",
                    fontSize: "lg",
                    weight: 700,
                    visibility: "all",
                  },
                },
                {
                  id: "node_tr1_p",
                  type: "Text",
                  props: {
                    content: [
                      {
                        text: "Organic cotton, recycled alloys, and plastic-free packaging.",
                      },
                    ],
                    align: "left",
                    color: "muted",
                    size: "base",
                    visibility: "all",
                  },
                },
              ],
            },
            {
              id: "node_tr2",
              type: "Container",
              props: {
                direction: "vertical",
                columns: 1,
                align: "stretch",
                justify: "start",
                gap: "XS",
                padding: "SM",
                visibility: "all",
              },
              children: [
                {
                  id: "node_tr2_h",
                  type: "Heading",
                  props: {
                    text: "⚡ Express Dispatch",
                    level: 3,
                    align: "left",
                    color: "text",
                    fontSize: "lg",
                    weight: 700,
                    visibility: "all",
                  },
                },
                {
                  id: "node_tr2_p",
                  type: "Text",
                  props: {
                    content: [
                      {
                        text: "Orders before 2 PM dispatch same-day with end-to-end tracking.",
                      },
                    ],
                    align: "left",
                    color: "muted",
                    size: "base",
                    visibility: "all",
                  },
                },
              ],
            },
            {
              id: "node_tr3",
              type: "Container",
              props: {
                direction: "vertical",
                columns: 1,
                align: "stretch",
                justify: "start",
                gap: "XS",
                padding: "SM",
                visibility: "all",
              },
              children: [
                {
                  id: "node_tr3_h",
                  type: "Heading",
                  props: {
                    text: "🛡️ Lifetime Guarantee",
                    level: 3,
                    align: "left",
                    color: "text",
                    fontSize: "lg",
                    weight: 700,
                    visibility: "all",
                  },
                },
                {
                  id: "node_tr3_p",
                  type: "Text",
                  props: {
                    content: [
                      {
                        text: "Guaranteed against craftsmanship defects for the life of the product.",
                      },
                    ],
                    align: "left",
                    color: "muted",
                    size: "base",
                    visibility: "all",
                  },
                },
              ],
            },
          ],
        },
      ],
    }),
  },
  {
    id: "ecom_preset_reviews",
    name: "Customer Reviews & Social Proof",
    category: "reviews",
    description: "3-column testimonial section with star ratings, quotes, and verified buyer badges.",
    iconName: "Star",
    badge: "Reviews",
    createSection: (): SiteNode => ({
      id: "sec_ecom_reviews",
      type: "Section",
      props: {
        direction: "vertical",
        columns: 1,
        align: "stretch",
        justify: "start",
        gap: "LG",
        padding: "XL",
        width: "contained",
        background: "surface",
        visibility: "all",
      },
      children: [
        {
          id: "node_rev_title",
          type: "Heading",
          props: {
            text: "Community Impressions",
            level: 2,
            align: "center",
            color: "text",
            fontSize: "4xl",
            weight: 700,
            visibility: "all",
          },
        },
        {
          id: "node_rev_grid",
          type: "Container",
          props: {
            direction: "horizontal",
            columns: 3,
            align: "stretch",
            justify: "start",
            gap: "LG",
            padding: "none",
            visibility: "all",
          },
          children: [
            {
              id: "node_rev_c1",
              type: "Container",
              props: {
                direction: "vertical",
                columns: 1,
                align: "stretch",
                justify: "start",
                gap: "SM",
                padding: "MD",
                visibility: "all",
              },
              children: [
                {
                  id: "node_rc1_stars",
                  type: "Text",
                  props: {
                    content: [{ text: "★★★★★ 5.0", marks: ["bold"] }],
                    align: "left",
                    color: "accent",
                    size: "base",
                    visibility: "all",
                  },
                },
                {
                  id: "node_rc1_quote",
                  type: "Text",
                  props: {
                    content: [
                      {
                        text: "“The quality easily rivals luxury brands at triple the price point. Remarkable craftsmanship.”",
                      },
                    ],
                    align: "left",
                    color: "text",
                    size: "base",
                    visibility: "all",
                  },
                },
                {
                  id: "node_rc1_name",
                  type: "Text",
                  props: {
                    content: [
                      {
                        text: "— Elena R., Verified Collector",
                        marks: ["italic"],
                      },
                    ],
                    align: "left",
                    color: "muted",
                    size: "sm",
                    visibility: "all",
                  },
                },
              ],
            },
            {
              id: "node_rev_c2",
              type: "Container",
              props: {
                direction: "vertical",
                columns: 1,
                align: "stretch",
                justify: "start",
                gap: "SM",
                padding: "MD",
                visibility: "all",
              },
              children: [
                {
                  id: "node_rc2_stars",
                  type: "Text",
                  props: {
                    content: [{ text: "★★★★★ 5.0", marks: ["bold"] }],
                    align: "left",
                    color: "accent",
                    size: "base",
                    visibility: "all",
                  },
                },
                {
                  id: "node_rc2_quote",
                  type: "Text",
                  props: {
                    content: [
                      {
                        text: "“Fast shipping, beautiful compostable unboxing, and the ceramic finish feels wonderful.”",
                      },
                    ],
                    align: "left",
                    color: "text",
                    size: "base",
                    visibility: "all",
                  },
                },
                {
                  id: "node_rc2_name",
                  type: "Text",
                  props: {
                    content: [
                      {
                        text: "— Michael D., Studio Lead",
                        marks: ["italic"],
                      },
                    ],
                    align: "left",
                    color: "muted",
                    size: "sm",
                    visibility: "all",
                  },
                },
              ],
            },
            {
              id: "node_rev_c3",
              type: "Container",
              props: {
                direction: "vertical",
                columns: 1,
                align: "stretch",
                justify: "start",
                gap: "SM",
                padding: "MD",
                visibility: "all",
              },
              children: [
                {
                  id: "node_rc3_stars",
                  type: "Text",
                  props: {
                    content: [{ text: "★★★★★ 5.0", marks: ["bold"] }],
                    align: "left",
                    color: "accent",
                    size: "base",
                    visibility: "all",
                  },
                },
                {
                  id: "node_rc3_quote",
                  type: "Text",
                  props: {
                    content: [
                      {
                        text: "“Customer service replied within 5 minutes when I had an address correction. Outstanding.”",
                      },
                    ],
                    align: "left",
                    color: "text",
                    size: "base",
                    visibility: "all",
                  },
                },
                {
                  id: "node_rc3_name",
                  type: "Text",
                  props: {
                    content: [
                      {
                        text: "— Sarah V., Verified Buyer",
                        marks: ["italic"],
                      },
                    ],
                    align: "left",
                    color: "muted",
                    size: "sm",
                    visibility: "all",
                  },
                },
              ],
            },
          ],
        },
      ],
    }),
  },
  {
    id: "ecom_preset_newsletter",
    name: "VIP Club / Newsletter Callout",
    category: "cta",
    description: "Sleek dark callout banner for email subscriber capture and VIP releases.",
    iconName: "Mail",
    badge: "Conversion",
    createSection: (): SiteNode => ({
      id: "sec_ecom_cta",
      type: "Section",
      props: {
        direction: "vertical",
        columns: 1,
        align: "stretch",
        justify: "center",
        gap: "MD",
        padding: "XL",
        width: "contained",
        background: "#0f172a",
        textColor: "#ffffff",
        visibility: "all",
      },
      children: [
        {
          id: "node_cta_h2",
          type: "Heading",
          props: {
            text: "Join the Collector's Club",
            level: 2,
            align: "center",
            color: "background",
            fontSize: "4xl",
            weight: 700,
            visibility: "all",
          },
        },
        {
          id: "node_cta_sub",
          type: "Text",
          props: {
            content: [
              {
                text: "Subscribe for private vault access, limited batch drops, and members-only seasonal incentives.",
              },
            ],
            align: "center",
            color: "surface",
            size: "lg",
            visibility: "all",
          },
        },
        {
          id: "node_cta_btn",
          type: "Button",
          props: {
            label: "Request Member Access",
            href: { type: "url", url: "/contact" },
            variant: "primary",
            size: "lg",
            align: "center",
            visibility: "all",
          },
        },
      ],
    }),
  },
];
